import { template } from '@belong/common';
import { ButtonBase, Image, Tabs, Text } from '@belong/ui';
import classNames from 'classnames/bind';
import BDate, { DATE_TYPES } from 'components/BDate/BDate';
import CurrentSupplyMapContainer from 'containers/CurrentSupplyMapContainer/CurrentSupplyMapContainer';
import { SPACE_TYPES } from 'corecomponents/Space/Space';
import Page from 'layouts/Page/Page';
import PostInspectionContentLayout from 'pages/PostInspectionFlow/PostInspectionContentLayout/PostInspectionContentLayout';
import { LastUpdated } from 'strings/common.strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import { getPricingAssignedEmployee } from '../utils';
import styles from './CurrentSupply.module.css';
import { ComparablesTable } from './comparables-table/comparables-table';

const cx = classNames.bind(styles);

const hasValidCoordinates = (home: Record<string, any>): boolean =>
  Boolean(
    home?.address?.coordinates?.latitude &&
    home?.address?.coordinates?.longitude,
  );

type CurrentSupplyProps = {
  stepResource: Record<string, any>;
  onPrevious: () => void;
  onSaveAndNext: () => void;
  stepConfigs: Array<Record<string, any>>;
  stepConfig: Record<string, any>;
};

const CurrentSupply = ({
  stepResource,
  onSaveAndNext,
  stepConfigs,
  stepConfig,
  onPrevious: _onPrevious,
}: CurrentSupplyProps) => {
  const { homeListingDetailsModel, details } = stepResource || {};

  const assignedEmployee = getPricingAssignedEmployee(stepResource);

  let homes = [
    {
      ...homeListingDetailsModel.basicInfo,
      ...homeListingDetailsModel,
      rentEstimate: details?.listing?.estimatedRent,
      current: true,
    },
  ];

  if (details?.relatedListings) {
    homes = [...homes, ...details.relatedListings];
  }

  const homesForMap = homes.filter(hasValidCoordinates);

  return (
    <PostInspectionContentLayout
      showCityBackground={false}
      noOverlay
      stepConfigs={stepConfigs}
      stepConfig={stepConfig}
      title={POST_INSPECTION_FLOW_STRINGS['map.title']}
      employee={assignedEmployee}
    >
      <Page.OffsetGrid
        ignoreOffset={[SPACE_TYPES.MD, SPACE_TYPES.LG, SPACE_TYPES.XL]}
      >
        <Tabs defaultIndex={0} hasBlurOnScroll={false}>
          <Tabs.TabList className="mb-sm flex justify-center">
            <Tabs.TabItem index={0} className="max-w-fit">
              List
            </Tabs.TabItem>
            {homesForMap.length > 0 && (
              <Tabs.TabItem index={1} className="max-w-fit">
                Map
              </Tabs.TabItem>
            )}
          </Tabs.TabList>

          <Tabs.PanelList>
            <Tabs.PanelItem>
              <ComparablesTable homes={homes} />
            </Tabs.PanelItem>
            {homesForMap.length > 0 && (
              <Tabs.PanelItem>
                <div className={cx('current-supply-wrapper')}>
                  <CurrentSupplyMapContainer homes={homesForMap} />
                </div>
              </Tabs.PanelItem>
            )}
          </Tabs.PanelList>
        </Tabs>
      </Page.OffsetGrid>

      <div className={cx('flex justify-end mt-xs mb-xl md:mb-xl')}>
        <Text variant="p1">
          {template(LastUpdated.last_updated_general, {
            date: (
              <BDate
                time={stepResource.details.relatedListingsLastModifiedOn}
                formatType={DATE_TYPES.DOTTED_MEDIUM}
              />
            ),
          })}
        </Text>
      </div>

      <div className="mb-2xl rounded border border-solid border-green bg-green-translucent-light p-sm">
        <div className="flex">
          <Image
            src="/pricing-flow/icon_lightbulb.svg"
            alt=""
            className="h-md w-[16px]"
          />
          <Text fontWeight="semibold" className="mb-sm ml-xs">
            {POST_INSPECTION_FLOW_STRINGS['pricing.marketing.tip.title']}
          </Text>
        </div>
        <Text>
          {
            POST_INSPECTION_FLOW_STRINGS[
              'pricing.marketing.tip.content-paragraph1'
            ]
          }
        </Text>
      </div>

      <div className="hidden justify-center md:flex">
        <PostInspectionContentLayout.CtaButton onClick={() => onSaveAndNext()}>
          {POST_INSPECTION_FLOW_STRINGS['map.button']}
        </PostInspectionContentLayout.CtaButton>
      </div>

      <div className="my-2xl flex w-full md:hidden">
        <ButtonBase
          className="rounded h-2xl flex-1 bg-green disabled:bg-gray"
          onClick={() => onSaveAndNext()}
        >
          <Text fontWeight="semibold" className="text-white">
            Next
          </Text>
        </ButtonBase>
      </div>
    </PostInspectionContentLayout>
  );
};

export default CurrentSupply;
